import { useContext } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import Carousel from '../../components/Carousel';
import DefaultCard from '../../components/DefaultCard';
import FingerTipsLinks from '../../components/FingertipsLinks/FingertipsLinks';
import {
  AreDashboardProvider,
  AreDashboardStore,
} from '../../providers/areDashboard';
import {
  ACCOUNT_ACTIVITY_ROUTE,
  HEALTH_INSURANCE_ROUTH,
  HEALTH_SUMMARY,
  LIFE_EVENTS_ROUTE,
  PENSION_PROFILE_ROUTE,
  PROFILE_ROUTE,
  SAVINGS_SUMMARY,
} from '../../routes/types';
import {
  addOrRemoveDaysToGivenDate,
  DateFormats,
  formatDate,
} from '../../utils/utils';
import CurrentAccruedPension from '../widgets/dashboards/CurrentAccruedPension';
import HealthSavingsAndSpending from '../widgets/dashboards/HealthSavingsAndSpending';
import HealthSnapshot from '../widgets/dashboards/HealthSnapshot';
import OtherCompanyBenefits from '../widgets/dashboards/OtherCompanyBenefits';
import SavingsSnapshot from '../widgets/dashboards/SavingsSnapshot';
import TotalRewardsHalf from '../widgets/dashboards/TotalRewardsHalf';

const linksData = [
  {
    linkHeader: 'More Details',
    route: `${HEALTH_SUMMARY}`,
  },
  {
    linkHeader: 'Savings Summary',
    route: `${SAVINGS_SUMMARY}`,
  },
];

const healthSnapshotLinksData = [
  {
    linkHeader: 'More Details',
    route: `${HEALTH_SUMMARY}`,
  },

  {
    linkHeader: 'Change Coverage',
    route: `${LIFE_EVENTS_ROUTE}`,
  },

  {
    linkHeader: 'Beneficiaries',
    route: `${PROFILE_ROUTE.slice(0, 9)}beneficiaries`,
  },

  {
    linkHeader: 'Family Members',
    route: `${PROFILE_ROUTE.slice(0, 9)}familymembers`,
  },
];

const linksDataCurrentAccruedPension = [
  {
    linkHeader: 'Account History',
    route: ACCOUNT_ACTIVITY_ROUTE,
  },

  {
    linkHeader: 'Pension Profile',
    route: PENSION_PROFILE_ROUTE,
  },
];

const healthData = [
  {
    title: 'HSA',
    subtitle: 'Health Saving Account',
    priceDescription: 'Total Value',
    balance: 36380.81,
  },

  {
    title: 'FSA',
    subtitle: 'Flexible Spendings Account',
    priceDescription: 'Available',
    balance: 399.75,
  },

  {
    title: 'HRA',
    subtitle: 'Health Reimbursement Arrangement',
    priceDescription: 'Available',
    balance: 2339.18,
  },
];

const currentAccruedPensionData = [
  {
    title: 'Cash Balance Pension',
    subtitle: `As of ${formatDate(new Date(), DateFormats.SHORT)}`,
    monthlyBenefitPriceDescription: 'Pay Age 65',
    balance: 53185.62,
    monthlyBenefit: 355.21,
  },
  {
    title: 'Qualified Plan',
    subtitle: `As of ${formatDate(new Date(), DateFormats.SHORT)}`,
    monthlyBenefitPriceDescription: 'Pay Age 65',
    balance: '',
    monthlyBenefit: 2643.98,
  },
  {
    title: 'Non-Qualified Plan',
    subtitle: `As of ${formatDate(new Date(), DateFormats.SHORT)}`,
    monthlyBenefitPriceDescription: 'Pay Age 65',
    balance: '',
    monthlyBenefit: 602.95,
  },
];

const dataCompanyContributions = {
  grid: {
    padding: {
      left: 0,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],

  tooltip: {
    enabled: false,
  },

  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$132,268`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const today = new Date();

const ActiveRetirementEligibleDashboardView = () => {
  const {
    fingertipLinks,
    companiesMessages,
    savingsSnapshot,
    coveredFamilyMembers,
    otherCompanyBenefits,
  } = useContext(AreDashboardStore) || {};

  const navigate = useNavigate();

  return (
    <Container className={'cnd-custom-container-large mb-12 px-lg-9 px-xl-0'}>
      <Row>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-7">
          <DefaultCard
            className="h-100 flex-column flex-sm-row "
            variant="blue-motif"
            leftHeading={
              <div className="text-sm-20-lg-32">Annual Enrollment</div>
            }
            leftSubheading={
              <div className="text-sm-12-lg-14 fw-light mb-7">
                Due:{' '}
                {formatDate(
                  addOrRemoveDaysToGivenDate(today, 30),
                  DateFormats.SHORT_MONTH_DAY_YEAR
                )}{' '}
                at 11:59PM EST
              </div>
            }
            button={
              <Button
                onClick={() => navigate(HEALTH_INSURANCE_ROUTH)}
                className="align-self-end"
                variant="primary-shadow"
                size="sm"
              >
                Enroll Now
              </Button>
            }
          />
        </Col>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-5">
          <DefaultCard
            isCurrentEvent
            className="flex-column"
            variant="blue"
            leftHeading={<div className="text-sm-20-lg-32">Current Events</div>}
            leftSubheading={
              <Container className="p-0">
                <Row className={'align-items-end'}>
                  <Col className={'col-8'}>
                    <Row>
                      <Col className="text-sm-12-lg-14 text-nowrap col-12 fw-normal">
                        Birth/Adoption
                      </Col>
                      <Col className="text-caption text-nowrap col-7 fw-light">
                        Deadline:{' '}
                        {formatDate(
                          addOrRemoveDaysToGivenDate(today, 25),
                          DateFormats.SHORT_MONTH_DAY_YEAR
                        )}
                      </Col>
                      <Col className="pb-3 text-caption text-nowrap col-7 fw-light">
                        Status: Completed
                      </Col>
                    </Row>
                  </Col>
                  <Col className={'col-4 mb-3 text-end'}>
                    <Button
                      onClick={() => navigate(HEALTH_INSURANCE_ROUTH)}
                      variant={'primary-shadow'}
                      size="sm"
                    >
                      Edit
                    </Button>
                  </Col>
                </Row>
              </Container>
            }
          />
        </Col>
      </Row>

      <Row className="d-flex justify-content-between m-0 p-0 mt-12">
        <Col className="m-0 p-0 d-flex flex-column col-12 col-lg-7 pe-lg-4">
          <SavingsSnapshot
            savingsSnapshot={savingsSnapshot}
            linksData={linksData}
          />
        </Col>
        <Col className="pe-lg-0 col-12 col-lg-5">
          <FingerTipsLinks data={fingertipLinks} className="p-0 pe-5" />
        </Col>
      </Row>

      <Row className="m-0 p-0 d-flex justify-content-between">
        <Col className="m-0 p-0 mt-12 mt-lg-8 pt-lg-6 col-12 col-lg-6 pe-lg-4">
          <HealthSnapshot
            transperant
            coveredFamilyMembers={coveredFamilyMembers}
            linksData={healthSnapshotLinksData}
          />
        </Col>
        <Col className="m-0 p-0 mt-12 mt-lg-8 pt-lg-6 col-12 col-lg-6 ps-lg-4">
          <CurrentAccruedPension
            currentAccruedPensionData={currentAccruedPensionData}
            linksData={linksDataCurrentAccruedPension}
          />
        </Col>
      </Row>

      <Row className="m-0 d-flex justify-content-between">
        <Col
          className={
            'm-0 p-0 d-flex flex-column pt-lg-6 col-12 col-lg-6 mt-10 mt-lg-6 pe-lg-4'
          }
        >
          <HealthSavingsAndSpending accruedPensionData={healthData} />
        </Col>
        <Col
          className={
            'm-0 p-0 d-flex flex-column pt-lg-6 col-12 col-lg-6 mt-10 mt-lg-6 ps-lg-4'
          }
        >
          <TotalRewardsHalf
            dataCompanyContributions={dataCompanyContributions}
          />
        </Col>
      </Row>
      <Row className="m-0 d-flex justify-content-between">
        <Col
          className={
            'm-0 p-0 d-flex flex-column pt-lg-6 col-12 col-lg-6 mt-10 mt-lg-6 pe-lg-4'
          }
        >
          <OtherCompanyBenefits
            companyBenefits={otherCompanyBenefits}
          />
        </Col>
      </Row>

      <Row className="m-0 mt-12 p-0">
        <Carousel data={companiesMessages} />
      </Row>
    </Container>
  );
};

const ActiveRetirementEligibleDashboard = () => {
  return (
    <AreDashboardProvider>
      <ActiveRetirementEligibleDashboardView />;
    </AreDashboardProvider>
  );
};

export default ActiveRetirementEligibleDashboard;
